import * as CollapsiblePrimitive from 'radix-ui/collapsible';
import type { ReactNode } from 'react';
import { cx } from '../cx';
import { Icon } from '../Icon/Icon';
import { MOTION } from '../motion';

/**
 * A DISCLOSURE OVER DATA: a heading row that opens and closes the block beneath it.
 *
 * CONTENT LAYER, therefore OPAQUE (DESIGN.md surface language, ios26-mechanisms.md's
 * component table). A collapsible holds record values and board-style lists; glass
 * belongs on chrome that floats above content, never under twelve columns of 15px
 * text. It draws no border and no fill of its own - it is a heading and a body, and
 * the Card or the column it sits in owns the surface.
 *
 * THE WHOLE HEADER IS THE CONTROL, and it is a real `<button>` with `aria-expanded`
 * (Radix's own trigger), because a chevron alone is a 16px target on a phone and a
 * heading that only responds to its icon is a heading half the team cannot open.
 * `--touch-target` is the floor.
 *
 * The chevron ROTATES rather than swapping glyphs: the two states are one object at
 * two angles, which is the spatial-continuity case DESIGN.md's motion rule allows, and
 * it collapses under `prefers-reduced-motion` with every other transition token.
 *
 * CONTROLLED ONLY. Open state on a record page is remembered per (object, group) in
 * `localStorage`, and a component that also held its own copy would be two sources of
 * truth for one disclosure.
 */
export interface CollapsibleSectionProps {
  readonly title: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Rendered right of the title, inside the button - a count, most often. */
  readonly meta?: ReactNode | undefined;
  /** Rendered right of everything, OUTSIDE the button: actions, never nested buttons. */
  readonly actions?: ReactNode | undefined;
  readonly children: ReactNode;
  readonly className?: string | undefined;
}

export function CollapsibleSection({
  title,
  open,
  onOpenChange,
  meta,
  actions,
  children,
  className,
}: CollapsibleSectionProps) {
  return (
    <CollapsiblePrimitive.Root open={open} onOpenChange={onOpenChange} className={className}>
      <div className="flex items-center gap-2">
        <CollapsiblePrimitive.Trigger
          className={cx(
            'pressable flex min-h-(--touch-target) flex-1 items-center gap-2 rounded-control px-2',
            'text-left hover:bg-surface-hover focus-visible:outline-2 focus-visible:outline-accent',
          )}
        >
          <Icon
            name="chevron-right"
            size="0.9rem"
            className={cx('shrink-0 text-ink-subtle', MOTION.turn, open && 'rotate-90')}
          />
          {/**
           * A SECTION TITLE IS NOT A CARD TITLE (audit P3-3).
           *
           * It was `text-2xs font-semibold tracking-widest text-ink-muted uppercase` -
           * character for character the `Card` header's own style - and every place this
           * component is used, it is used INSIDE a Card: the record page's DETAILS card
           * holds "Identity", "Relationship" and "Commercial"; the RELATED card holds
           * "Calls" and "People". So a card and its children announced themselves at the
           * same size, weight, case and colour, and DESIGN.md's "one hierarchy per
           * screen" had two levels drawn as one.
           *
           * The ladder now reads top to bottom: a CARD title is the quiet chrome label
           * that names the surface (13px, tracked caps, muted); a SECTION title is a
           * content heading (14px, semibold, sentence case, full ink); a FIELD label is
           * 14px medium muted. Dropping the caps and the tracking is what does the work -
           * a colour step alone would have left two labels of the same shape.
           */}
          <span className="text-xs font-semibold text-ink">{title}</span>
          {meta}
        </CollapsiblePrimitive.Trigger>
        {actions}
      </div>
      <CollapsiblePrimitive.Content>{children}</CollapsiblePrimitive.Content>
    </CollapsiblePrimitive.Root>
  );
}
